
import { useState, useEffect, useRef } from 'react'
import { smartleadAPI, Campaign } from '@/lib/smartlead-api'
import { useDashboard } from '@/contexts/DashboardContext'
import { dashboardCache, systemHealth, activityService } from '@/lib/supabase-services'
import type { DashboardMetrics, ActivityRecord, SystemHealthData } from '@/lib/supabase-services'
import { supabase } from '@/integrations/supabase/client'
import { isHealthyMailbox } from '@/lib/client-domain-categorizer'
import { ComprehensiveSyncService } from '@/lib/comprehensive-sync'

interface ExtendedDashboardMetrics extends DashboardMetrics {
  loading: boolean
  error: string | null
  usingCache: boolean
  loadingStatus?: string
  mailboxesLoading?: boolean
  refreshing?: boolean
  activities?: ActivityRecord[]
  systemHealth?: SystemHealthData
}

interface UseDashboardDataOptions {
  /** When false, only loads cached data — skips SmartLead API calls and heavy fetches.
   *  Use enabled=false on non-dashboard pages where DashboardHeader only needs lastUpdated/loading state. */
  enabled?: boolean
}

export function useDashboardData(options: UseDashboardDataOptions = {}) {
  const { enabled = true } = options
  const { state } = useDashboard()
  const subscriptionRef = useRef<any>(null)
  const isSubscribedRef = useRef(false)
  
  const [data, setData] = useState<ExtendedDashboardMetrics>({
    totalClients: 0,
    activeCampaigns: 0,
    activeTriggers: 0,
    activeVariants: 0,
    totalMailboxes: 0,
    todaysEmails: 0,
    overallReplyRate: 0,
    qualifiedLeads: 0,
    healthyMailboxes: 0,
    activeMailboxes: 0,
    activeDomains: 0,
    activeAlerts: state.alerts.filter(a => a.status === 'active').length,
    loading: true,
    error: null,
    lastUpdated: null,
    usingCache: false,
    loadingStatus: 'Initializing...',
    mailboxesLoading: false,
    refreshing: false,
    activities: [],
    systemHealth: undefined
  })

  const fetchDashboardData = async (isBackgroundRefresh = false) => {
    try {
      if (isBackgroundRefresh) {
        setData(prev => ({ 
          ...prev, 
          refreshing: true,
          error: null
        }))
      } else {
        setData(prev => ({ 
          ...prev, 
          loading: true, 
          error: null, 
          loadingStatus: 'Starting dashboard data fetch...', 
          mailboxesLoading: true,
          usingCache: false,
          refreshing: false
        }))
      }
      
      // Check if Smartlead is configured
      if (!smartleadAPI.isConfigured()) {
        const mockData = {
          totalClients: 8,
          activeCampaigns: 15, // Fixed: Should match DEMO_CAMPAIGNS count
          activeTriggers: 3,
          activeVariants: 67,
          totalMailboxes: 156, // Reverted: Keep Phase 1 consistency
          todaysEmails: 2847,
          overallReplyRate: 12.3,
          qualifiedLeads: 47,
          healthyMailboxes: 142, // Reverted: Keep Phase 1 consistency
          activeMailboxes: 142,
          activeDomains: 50,
          activeAlerts: state.alerts.filter(a => a.status === 'active').length,
          loading: false,
          error: 'Using demo data - Smartlead API not configured',
          lastUpdated: new Date(),
          usingCache: false,
          mailboxesLoading: false,
          refreshing: false,
          activities: [],
          systemHealth: undefined
        }
        setData(mockData)
        return
      }
      
      if (!isBackgroundRefresh) {
        setData(prev => ({ ...prev, loadingStatus: 'Fetching campaigns, mailboxes and clients...' }))
      }
      
      const startTime = Date.now()
      
      // Note: Removed dashboard_metrics cache clearing to prevent 406 errors
      // The table will auto-expire based on expires_at column
      
      // Fetch mailbox count (using exact count to bypass Supabase's default 1000 row limit)
      const mailboxCountPromise = supabase
        .from('mailboxes')
        .select('*', { count: 'exact', head: true })

      // Fetch active mailbox count
      const activeMailboxCountPromise = supabase
        .from('mailboxes')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'ACTIVE')

      // Fetch active domain count (domains table uses 'Active' not 'ACTIVE')
      const activeDomainCountPromise = supabase
        .from('domains')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Active')

      // Fetch mailbox health fields with pagination to bypass 1000 row limit
      const fetchAllMailboxHealth = async () => {
        const allMailboxes: any[] = []
        const pageSize = 1000
        let offset = 0
        while (true) {
          const { data, error } = await supabase
            .from('mailboxes')
            .select('warmup_reputation, smtp_success, imap_success')
            .range(offset, offset + pageSize - 1)
          if (error) throw error
          if (!data || data.length === 0) break
          // Map DB column names to what isHealthyMailbox expects
          allMailboxes.push(...data.map(m => ({
            reputation_score: m.warmup_reputation,
            smtp_success: m.smtp_success,
            imap_success: m.imap_success
          })))
          if (data.length < pageSize) break
          offset += pageSize
        }
        return allMailboxes
      }
      const mailboxHealthPromise = fetchAllMailboxHealth()
      
      // Fetch local campaigns from Supabase
      const localCampaignsPromise = supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false })
      
      const [campaignsResult, localCampaignsResult, mailboxCountResult, mailboxHealthResult, clientsResult, activeMailboxCountResult, activeDomainCountResult] = await Promise.allSettled([
        smartleadAPI.fetchCampaigns(),
        localCampaignsPromise,
        mailboxCountPromise,
        mailboxHealthPromise,
        smartleadAPI.fetchClients(),
        activeMailboxCountPromise,
        activeDomainCountPromise
      ])

      const responseTime = Date.now() - startTime

      const campaigns = campaignsResult.status === 'fulfilled' ? campaignsResult.value : []
      const localCampaigns = localCampaignsResult.status === 'fulfilled' ? localCampaignsResult.value.data || [] : []
      const mailboxCount = mailboxCountResult.status === 'fulfilled' ? mailboxCountResult.value.count || 0 : 0
      const mailboxHealthData = mailboxHealthResult.status === 'fulfilled' ? mailboxHealthResult.value : []
      const clients = clientsResult.status === 'fulfilled' ? clientsResult.value : []
      const activeMailboxCount = activeMailboxCountResult.status === 'fulfilled' ? activeMailboxCountResult.value.count || 0 : 0
      const activeDomainCount = activeDomainCountResult.status === 'fulfilled' ? activeDomainCountResult.value.count || 0 : 0

      // Run comprehensive sync in background (non-blocking) with rate limiting
      if (campaigns.length > 0) {
        const lastSyncKey = 'lastComprehensiveSync'
        const lastSync = localStorage.getItem(lastSyncKey)
        const now = Date.now()
        const fourHours = 4 * 60 * 60 * 1000

        if (!lastSync || (now - parseInt(lastSync)) > fourHours) {
          localStorage.setItem(lastSyncKey, now.toString())
          ComprehensiveSyncService.syncAll().catch(() => {})
        }
      }
      
      const failedCalls = []
      if (campaignsResult.status === 'rejected') {
        console.error('Failed to fetch Smartlead campaigns:', campaignsResult.reason)
        failedCalls.push('smartlead_campaigns')
      }
      if (localCampaignsResult.status === 'rejected') {
        console.error('Failed to fetch local campaigns:', localCampaignsResult.reason)
        failedCalls.push('local_campaigns')
      }
      if (mailboxCountResult.status === 'rejected') {
        console.error('Failed to fetch mailbox count:', mailboxCountResult.reason)
        failedCalls.push('mailboxes')
      }
      if (mailboxHealthResult.status === 'rejected') {
        console.error('Failed to fetch mailbox health data:', mailboxHealthResult.reason)
        failedCalls.push('mailbox_health')
      }
      if (clientsResult.status === 'rejected') {
        console.error('Failed to fetch clients:', clientsResult.reason)
        failedCalls.push('clients')
      }
      
      const activeCampaigns = localCampaigns.filter(campaign => campaign.status === 'active' && campaign.campaign_type !== 'triggers').length
      const activeTriggers = localCampaigns.filter(campaign => campaign.status === 'active' && campaign.campaign_type === 'triggers').length
      
      // Active Variants: Smartlead campaigns with ACTIVE status
      const activeVariants = campaigns.filter(c => c.status === 'ACTIVE').length
      const totalMailboxes = mailboxCount
      const healthyMailboxes = mailboxHealthData.filter(m => isHealthyMailbox(m)).length
      
      // Fetch today's emails directly from analytics in dashboard
      const { data: emailAnalyticsData } = await supabase.rpc('get_email_analytics', { days_back: 30 })
      const analyticsData = emailAnalyticsData as { today: number; last_30_days: number; last_30_days_replies: number } | null
      const todaysEmails = analyticsData?.today || 0

      // Calculate reply rate from actual analytics data (replies / emails sent over last 30 days)
      const totalSent30d = analyticsData?.last_30_days || 0
      const totalReplies30d = analyticsData?.last_30_days_replies || 0
      const overallReplyRate = totalSent30d > 0
        ? Math.round((totalReplies30d / totalSent30d) * 1000) / 10
        : 0
      const qualifiedLeads = campaigns.reduce((sum, c) => 
        sum + (c.meetings_qualified || 0), 0
      )
      
      const newMetrics: DashboardMetrics = {
        totalClients: clients.filter(c => c.status === 'Active').length,
        activeCampaigns: activeCampaigns, // Local campaigns with status = 'active' (excluding triggers)
        activeTriggers: activeTriggers, // Local campaigns with campaign_type = 'triggers' and status = 'active'
        activeVariants: activeVariants, // Smartlead campaigns with status = 'ACTIVE'
        totalMailboxes,
        todaysEmails, // Using analytics data for consistency
        overallReplyRate,
        qualifiedLeads,
        healthyMailboxes,
        activeMailboxes: activeMailboxCount,
        activeDomains: activeDomainCount,
        activeAlerts: state.alerts.filter(a => a.status === 'active').length,
        lastUpdated: new Date(),
        fromCache: false
      }
      
      const [activities, healthData] = await Promise.all([
        activityService.getRecentActivities(10),
        systemHealth.getSystemHealth()
      ])
      
      // Cache the metrics including todaysEmails
      await dashboardCache.cacheDashboardMetrics(newMetrics)
      
      await systemHealth.recordHealthCheck({
        smartleadStatus: failedCalls.length === 0 ? 'healthy' : 'degraded',
        supabaseStatus: 'healthy',
        responseTime,
        lastSync: new Date(),
        rateLimitUsage: 0,
        errorsLastHour: failedCalls.length
      })
      
      await activityService.addActivity({
        type: 'system',
        title: 'Dashboard Data Refreshed',
        description: `Updated metrics: ${activeCampaigns} campaigns, ${totalMailboxes} mailboxes, ${todaysEmails} emails today`,
        icon: '🔄'
      })
      
      const finalData = {
        ...newMetrics,
        loading: false,
        error: failedCalls.length > 0 ? `Some data unavailable (${failedCalls.join(', ')})` : null,
        usingCache: false,
        mailboxesLoading: false,
        refreshing: false,
        activities,
        systemHealth: healthData
      }
      
      setData(finalData)

    } catch (error) {
      console.error('[useDashboardData] Dashboard data fetch failed:', error)
      
      if (isBackgroundRefresh) {
        setData(prev => ({
          ...prev,
          refreshing: false,
          error: `Background refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        }))
        return
      }
      
      const cachedMetrics = await dashboardCache.getCachedDashboardMetrics()
      const [activities, healthData] = await Promise.all([
        activityService.getRecentActivities(10),
        systemHealth.getSystemHealth()
      ])
      
      if (cachedMetrics) {
        setData({
          ...cachedMetrics,
          loading: false,
          error: 'Using cached data - API temporarily unavailable',
          usingCache: true,
          mailboxesLoading: false,
          refreshing: false,
          activities,
          systemHealth: healthData
        })
        return
      }
      
      setData({
        totalClients: 8,
        activeCampaigns: 15, // Fixed: Should match DEMO_CAMPAIGNS count
        activeTriggers: 3,
        activeVariants: 67,
        totalMailboxes: 156,
        todaysEmails: 2847,
        overallReplyRate: 12.3,
        qualifiedLeads: 47,
        healthyMailboxes: 142,
        activeMailboxes: 142,
        activeDomains: 50,
        activeAlerts: state.alerts.filter(a => a.status === 'active').length,
        loading: false,
        error: 'API unavailable - showing demo data',
        lastUpdated: new Date(),
        usingCache: false,
        mailboxesLoading: false,
        refreshing: false,
        activities,
        systemHealth: healthData
      })
    }
  }

  useEffect(() => {
    async function loadDashboardData() {
      const cachedMetrics = await dashboardCache.getCachedDashboardMetrics()

      if (cachedMetrics) {
        const [activities, healthData] = await Promise.all([
          activityService.getRecentActivities(10),
          systemHealth.getSystemHealth()
        ])

        setData({
          ...cachedMetrics,
          loading: false,
          error: null,
          usingCache: true,
          activities,
          systemHealth: healthData
        })

        // Only do background refresh if enabled (dashboard page)
        if (enabled) {
          setTimeout(() => fetchDashboardData(true), 1000)
        }
      } else if (enabled) {
        // Only fetch fresh data if enabled — non-dashboard pages just show empty/cached state
        await fetchDashboardData(false)
      } else {
        // Not enabled and no cache — set loading to false with empty state
        setData(prev => ({ ...prev, loading: false }))
      }
    }

    loadDashboardData()

    // Only set up periodic refresh on dashboard pages
    if (!enabled) return

    const interval = setInterval(() => {
      fetchDashboardData(true)
    }, 15 * 60 * 1000)

    return () => {
      clearInterval(interval)
    }
  }, [enabled])

  // Real-time subscription — only on dashboard pages where enabled=true
  useEffect(() => {
    if (!enabled) return

    // Clean up any existing subscription
    if (subscriptionRef.current && isSubscribedRef.current) {
      try {
        subscriptionRef.current.unsubscribe()
      } catch (error) {
        console.warn('Error unsubscribing:', error)
      }
      subscriptionRef.current = null
      isSubscribedRef.current = false
    }

    if (!isSubscribedRef.current) {
      const channelName = `dashboard-activities-${Math.random().toString(36).substr(2, 9)}`

      const activitySubscription = supabase
        .channel(channelName)
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'recent_activity' },
          (payload) => {
            const newActivity: ActivityRecord = {
              id: payload.new.id,
              type: payload.new.activity_type,
              title: payload.new.title,
              description: payload.new.description,
              icon: payload.new.icon,
              timestamp: new Date(payload.new.created_at)
            }

            setData(prev => ({
              ...prev,
              activities: [newActivity, ...(prev.activities || []).slice(0, 9)]
            }))
          }
        )
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'obsync_dynamic_reports' },
          (payload) => {
            if (payload.new.event_type === 'EMAIL_SENT') {
              setTimeout(() => fetchDashboardData(true), 1000)
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            isSubscribedRef.current = true
          }
        })

      subscriptionRef.current = activitySubscription
    }

    return () => {
      if (subscriptionRef.current && isSubscribedRef.current) {
        try {
          subscriptionRef.current.unsubscribe()
        } catch (error) {
          console.warn('Error during cleanup:', error)
        }
        subscriptionRef.current = null
        isSubscribedRef.current = false
      }
    }
  }, [enabled])

  // Update activeAlerts when state.alerts changes
  useEffect(() => {
    setData(prev => ({
      ...prev,
      activeAlerts: state.alerts.filter(a => a.status === 'active').length
    }))
  }, [state.alerts])

  const refresh = async () => {
    await fetchDashboardData(true)
  }

  return { ...data, refresh }
}
